import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    HashRouter,
    Navigate,
    Route,
    Routes,
    useLocation,
} from "react-router-dom";
import MainView from "./views/main";
import DocumentsView from "./views/documents";
import OnboardingView from "./views/onboarding";
import NotFound from "./components/notFound";
import { RootState } from "./reducers/store";
import { searchSubjects } from "./actions/onboarding";
import {
    AdapterDateFns,
    LocalizationProvider,
} from "@vapor/react-x-date-pickers";
import itLocale from "date-fns/locale/it";
import { ModefinancePolicyMessage } from "./components/policy";
import Notification from "./components/vapor/notification";
import { FormattedMessage } from "react-intl";
import { getModebisConfig } from "./actions/config";

interface RequireAuthProps {
    component?: any;
    onboarding?: boolean;
    setOnboarding: (val: boolean) => void;
    setShowNotification: (val: any) => void;
    showNotification: any;
}

const RequireAuth = (props: RequireAuthProps) => {
    const dispatch = useDispatch();
    const auth = useSelector((state: RootState) => state.auth);
    const onboarding = useSelector((state: RootState) => state.onboarding);
    const modebisconfig = useSelector(
        (state: RootState) => state.modebisconfig,
    );
    let location = useLocation();
    if (props.onboarding || onboarding.forceOnboarding) {
        if (!onboarding.forceOnboarding) {
            dispatch({ type: "FORCE_ONBOARDING" });
        }
        return (
            <OnboardingView
                onComplete={() => {
                    dispatch({ type: "REMOVE_FORCE_ONBOARDING" });
                    props.setOnboarding(false);
                    window.location.reload();
                }}
            />
        );
    }
    if (!auth.token) {
        return <Navigate to="/not-found" state={{ from: location }} replace />;
    }
    if (!onboarding.subjectIsLoaded && modebisconfig.version === "FULL") {
        return <></>;
    }
    if (props.component) {
        return props.component;
    }
    return (
        <MainView
            showNotification={props.showNotification}
            setShowNotification={props.setShowNotification}
        />
    );
};

const MainRoutes = (props: any) => {
    const parent = useSelector((state: RootState) => state.parent);
    const onboardingRedux = useSelector((state: RootState) => state.onboarding);
    const dispatch: any = useDispatch();
    const [showNotification, setShowNotification] = useState(false);
    const [onboarding, setOnboarding] = useState(false);
    const isLoadingPackage = useSelector(
        (state: RootState) => state.userPackage.isLoadingPackage,
    );

    useEffect(() => {
        if (parent.item.taxId && parent.user.token && !onboarding) {
            dispatch(getModebisConfig())
                .then((resc: any) => {
                    dispatch(searchSubjects())
                        .then((res: any) => {
                            if (
                                !res?.subject?.has_id_card &&
                                !res?.consent?.acceptGenericTerms
                            ) {
                                setOnboarding(true);
                            } else {
                                if (
                                    !res?.subject?.has_id_card &&
                                    resc.version === "FULL"
                                ) {
                                    setOnboarding(true);
                                }
                            }
                        })
                        .catch((e: any) => {
                            setOnboarding(true);
                        });
                })
                .catch((errc: any) => {
                    console.log("err", errc);
                });
        }
    }, [
        dispatch,
        parent.item.taxId,
        parent.user.token,
        onboarding,
        setOnboarding,
    ]);

    return (
        <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={itLocale}
        >
            <HashRouter basename="/">
                <Routes>
                    <Route path="*" element={<Navigate to="/#" />} />
                    <Route path="/not-found" element={<NotFound />}></Route>
                    <Route
                        path="/"
                        element={
                            <RequireAuth
                                showNotification={showNotification}
                                setShowNotification={setShowNotification}
                                setOnboarding={setOnboarding}
                                onboarding={onboarding}
                            />
                        }
                    ></Route>
                    <Route
                        path="/documenti"
                        element={
                            <RequireAuth
                                setShowNotification={setShowNotification}
                                showNotification={showNotification}
                                setOnboarding={setOnboarding}
                                onboarding={onboarding}
                                component={<DocumentsView />}
                            />
                        }
                    ></Route>
                </Routes>
            </HashRouter>
            {showNotification && (
                <Notification
                    // max ms value allowed (see at https://stackoverflow.com/questions/3468607/why-does-settimeout-break-for-large-millisecond-delay-values)
                    duration={6000}
                    title={<FormattedMessage id="feedbacks.buy.okTitle" />}
                    variant={"success"}
                    onClose={() => {
                        setShowNotification(false);
                    }}
                >
                    <FormattedMessage id="feedbacks.buy.okMessage" />
                </Notification>
            )}
            {!props.onboarding &&
                !isLoadingPackage &&
                !onboardingRedux.forceOnboarding && (
                    <ModefinancePolicyMessage />
                )}
        </LocalizationProvider>
    );
};

export default MainRoutes;
